/*
 * geo.js — the arithmetic of a GPS track: distance on the ellipsoid, moving
 * time, elevation gain, speed, grade, splits, simplification, spike search.
 *
 *   GpsGeo.series(track)          → flat Series over all segments (see below)
 *   GpsGeo.stats(series, opts)    → the numbers a person asks for
 *   GpsGeo.climb(series, from, to, opts) → { gain, loss, method, threshold }
 *
 * Series = { n, lat, lon, ele, time, dist (cumulative m, NOT across segment
 *            gaps), segStart:[i…], ch:{…}, geoDist }
 *
 * The numbers are the product, so each rule below says what it is measured
 * against. The oracle for distance, moving time and ascent is the device
 * itself: a FIT activity carries the totals the watch computed (session
 * message), and tests/js/gps-track-stats.test.mjs holds our numbers to them on
 * real files from Garmin, Suunto and Wahoo units.
 */
(function () {
    'use strict';
    var root = typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : this);

    var A = 6378137.0, F = 1 / 298.257223563, E2 = F * (2 - F), RAD = Math.PI / 180;

    /* Distance between two fixes. Consecutive fixes are metres apart, where the
     * ellipsoid is locally flat to parts per billion: the meridional (M) and
     * prime-vertical (N) radii at the mid-latitude give the exact local metric.
     * Legs longer than 20 km (routes, sparse KML lines) go through Vincenty. */
    function dist(lat1, lon1, lat2, lon2) {
        var dLat = (lat2 - lat1) * RAD;
        var dLon = lon2 - lon1;
        if (dLon > 180) dLon -= 360; else if (dLon < -180) dLon += 360;
        dLon *= RAD;
        var phi = (lat1 + lat2) * 0.5 * RAD;
        var s = Math.sin(phi);
        var w = 1 - E2 * s * s;
        var N = A / Math.sqrt(w);
        var M = N * (1 - E2) / w;
        var dy = M * dLat, dx = N * Math.cos(phi) * dLon;
        var d = Math.sqrt(dx * dx + dy * dy);
        return d < 20000 ? d : vincenty(lat1, lon1, lat2, lon2);
    }

    function vincenty(lat1, lon1, lat2, lon2) {
        var B = A * (1 - F);
        var L = (lon2 - lon1) * RAD;
        var U1 = Math.atan((1 - F) * Math.tan(lat1 * RAD)), U2 = Math.atan((1 - F) * Math.tan(lat2 * RAD));
        var sinU1 = Math.sin(U1), cosU1 = Math.cos(U1), sinU2 = Math.sin(U2), cosU2 = Math.cos(U2);
        var lambda = L, prev, iter = 0, sinS, cosS, sigma, cos2a, cos2sm;
        do {
            var sinL = Math.sin(lambda), cosL = Math.cos(lambda);
            sinS = Math.sqrt((cosU2 * sinL) * (cosU2 * sinL) + (cosU1 * sinU2 - sinU1 * cosU2 * cosL) * (cosU1 * sinU2 - sinU1 * cosU2 * cosL));
            if (sinS === 0) return 0;
            cosS = sinU1 * sinU2 + cosU1 * cosU2 * cosL;
            sigma = Math.atan2(sinS, cosS);
            var sinA = cosU1 * cosU2 * sinL / sinS;
            cos2a = 1 - sinA * sinA;
            cos2sm = cos2a ? cosS - 2 * sinU1 * sinU2 / cos2a : 0;
            var C = F / 16 * cos2a * (4 + F * (4 - 3 * cos2a));
            prev = lambda;
            lambda = L + (1 - C) * F * sinA * (sigma + C * sinS * (cos2sm + C * cosS * (-1 + 2 * cos2sm * cos2sm)));
        } while (Math.abs(lambda - prev) > 1e-12 && ++iter < 200);
        if (iter >= 200) { // near-antipodal: fall back to a sphere, it is still the right order of magnitude
            return 6371008.8 * sigma;
        }
        var u2 = cos2a * (A * A - B * B) / (B * B);
        var AA = 1 + u2 / 16384 * (4096 + u2 * (-768 + u2 * (320 - 175 * u2)));
        var BB = u2 / 1024 * (256 + u2 * (-128 + u2 * (74 - 47 * u2)));
        var dS = BB * sinS * (cos2sm + BB / 4 * (cosS * (-1 + 2 * cos2sm * cos2sm) - BB / 6 * cos2sm * (-3 + 4 * sinS * sinS) * (-3 + 4 * cos2sm * cos2sm)));
        return B * AA * (sigma - dS);
    }

    // ── flatten a track ────────────────────────────────────────────────────
    function series(trk) {
        var segs = trk.segs || [];
        var n = 0, i, k;
        for (i = 0; i < segs.length; i++) n += segs[i].n;
        var lat = new Float64Array(n), lon = new Float64Array(n), dst = new Float64Array(n);
        var ele = null, time = null, ch = {}, segStart = [], devDist = null;
        var anyEle = segs.some(function (s) { return !!s.ele; });
        var anyTime = segs.some(function (s) { return !!s.time; });
        if (anyEle) ele = new Float32Array(n).fill(NaN);
        if (anyTime) time = new Float64Array(n).fill(NaN);
        var chNames = {};
        segs.forEach(function (s) { for (var c in s.ch) chNames[c] = 1; });
        for (var c in chNames) ch[c] = new Float32Array(n).fill(NaN);
        var o = 0, acc = 0;
        for (i = 0; i < segs.length; i++) {
            var s = segs[i];
            segStart.push(o);
            for (k = 0; k < s.n; k++) {
                var j = o + k;
                lat[j] = s.lat[k]; lon[j] = s.lon[k];
                if (k > 0) acc += dist(s.lat[k - 1], s.lon[k - 1], s.lat[k], s.lon[k]);
                dst[j] = acc;
                if (s.ele) ele[j] = s.ele[k];
                if (s.time) time[j] = s.time[k];
                for (var cn in s.ch) ch[cn][j] = s.ch[cn][k];
            }
            o += s.n;
        }
        // Device distance (FIT/TCX cumulative) is what the watch and its app show;
        // keep it as the primary distance when it covers the track.
        if (ch.dist) {
            var cover = 0;
            for (i = 0; i < n; i++) if (!isNaN(ch.dist[i])) cover++;
            if (cover > n * 0.9) {
                devDist = new Float64Array(n);
                var last = 0, base = NaN;
                for (i = 0; i < n; i++) {
                    var v = ch.dist[i];
                    if (!isNaN(v)) { if (isNaN(base)) base = v; last = v - base; }
                    devDist[i] = last;
                }
            }
        }
        return { n: n, lat: lat, lon: lon, ele: ele, time: time, dist: devDist || dst, geoDist: dst, devDist: !!devDist, segStart: segStart, ch: ch };
    }

    function isSegStart(sr, i) {
        for (var k = 0; k < sr.segStart.length; k++) if (sr.segStart[k] === i) return true;
        return false;
    }

    // ── elevation gain ─────────────────────────────────────────────────────
    /* 1. Resample elevation onto a fixed DISTANCE grid. Windows counted in
     *    points depend on the recording rate (1 s vs «smart» recording), windows
     *    in metres do not — and a person standing still does not climb.
     * 2. Median over a small window: kills single-fix spikes, keeps steps.
     * 3. Zig-zag hysteresis: a climb counts from valley to peak once the track
     *    has come back down by the threshold. Unlike «count every step above T»
     *    it does not throw away the last partial step of every climb.
     * Threshold: 2 m for a barometric altimeter, larger for GPS elevation —
     * the split Strava publishes. Which one a file is gets decided by its own
     * noise (second difference on the grid), not by the extension. */
    function resampleEle(sr, from, to, step) {
        var d = sr.dist, e = sr.ele, out = [];
        if (!e) return out;
        var i = from;
        while (i <= to && isNaN(e[i])) i++;
        if (i > to) return out;
        var x = d[i];
        var j = i;
        var end = d[to];
        while (x <= end) {
            while (j < to && d[j + 1] < x) j++;
            // find valid neighbours around x
            var a = j, b = Math.min(j + 1, to);
            while (a > from && isNaN(e[a])) a--;
            while (b < to && isNaN(e[b])) b++;
            var ea = e[a], eb = e[b];
            var v;
            if (isNaN(ea)) v = eb; else if (isNaN(eb) || d[b] === d[a]) v = ea;
            else v = ea + (eb - ea) * Math.max(0, Math.min(1, (x - d[a]) / (d[b] - d[a])));
            if (!isNaN(v)) out.push(v);
            x += step;
        }
        return out;
    }

    function median(arr, w) {
        if (w <= 1) return arr.slice();
        var h = w >> 1, out = new Array(arr.length), buf = [];
        for (var i = 0; i < arr.length; i++) {
            buf.length = 0;
            for (var k = Math.max(0, i - h); k <= Math.min(arr.length - 1, i + h); k++) buf.push(arr[k]);
            buf.sort(function (p, q) { return p - q; });
            out[i] = buf[buf.length >> 1];
        }
        return out;
    }

    /* Peaks and valleys with hysteresis T: a pivot is confirmed only once the
     * series has moved back by T from it; gain is the sum of confirmed
     * valley→peak swings (the last swing counts as it stands). */
    function zigzag(v, T) {
        var gain = 0, loss = 0, n = v.length;
        if (n < 2) return { gain: 0, loss: 0 };
        var trend = 0, lo = v[0], hi = v[0], pivot = v[0];
        for (var i = 1; i < n; i++) {
            var x = v[i];
            if (trend === 0) {
                if (x > hi) hi = x;
                if (x < lo) lo = x;
                if (hi - lo >= T) {
                    if (x === hi) { trend = 1; pivot = lo; }
                    else { trend = -1; pivot = hi; }
                }
            } else if (trend === 1) {
                if (x > hi) hi = x;
                else if (hi - x >= T) { gain += hi - pivot; pivot = hi; trend = -1; lo = x; }
            } else {
                if (x < lo) lo = x;
                else if (x - lo >= T) { loss += pivot - lo; pivot = lo; trend = 1; hi = x; }
            }
        }
        if (trend === 1) gain += hi - pivot;
        else if (trend === -1) loss += pivot - lo;
        return { gain: gain, loss: loss };
    }

    function noiseOf(v) {
        // robust std of the second difference: a barometer is smooth (≈0.1–0.4 m),
        // GPS altitude jitters by metres.
        if (v.length < 8) return 0;
        var dd = [];
        for (var i = 1; i < v.length - 1; i++) dd.push(Math.abs(v[i + 1] - 2 * v[i] + v[i - 1]));
        dd.sort(function (a, b) { return a - b; });
        return dd[dd.length >> 1] * 1.4826;
    }

    /* Barometer or GPS? A barometric altimeter changes by its quantum (0.1–0.2 m)
     * from one second to the next; GPS altitude jitters by metres. The median
     * |second difference| at the native rate (fixes ≤ 3 s apart) separates them
     * with a wide margin — measured on the corpus: 14 barometric FIT files
     * ≤ 0.6 m (p90 ≤ 0.8), GPS altitude 1.2–3 m on real handhelds and IGC
     * loggers, ≥ 5 m with simulated GPS error on the same tracks. */
    function jitter(ele, time, from, to) {
        var dd = [];
        for (var i = from + 1; i < to; i++) {
            var a = ele[i - 1], b = ele[i], c = ele[i + 1];
            if (isNaN(a) || isNaN(b) || isNaN(c)) continue;
            var t0 = time[i - 1], t1 = time[i], t2 = time[i + 1];
            if (!(t1 > t0 && t2 > t1 && t1 - t0 <= 3000 && t2 - t1 <= 3000)) continue;
            dd.push(Math.abs(c - 2 * b + a));
        }
        if (dd.length < 30) return null;
        dd.sort(function (x, y) { return x - y; });
        return { med: dd[dd.length >> 1] * 1.4826, p90: dd[Math.floor(dd.length * 0.9)] };
    }

    function timeGrid(ele, time, a, b, stepMs) {
        var v = [], next = null;
        for (var i = a; i <= b; i++) {
            var t = time[i], e = ele[i];
            if (isNaN(t) || isNaN(e)) continue;
            if (next === null || t >= next) { v.push(e); next = t + stepMs; }
        }
        return v;
    }

    /* Parameters, each measured (tests/js/gps-track-stats.test.mjs):
     *   baro — 10 s grid, no median, T 2 m: 5 % mean error against the device's
     *          own ascent on 11 activities, bias +0.3 %.
     *   gps  — 10 s grid, median 9, T 15 m: the least-bad setting under
     *          simulated GPS error (σ 6 m, correlated over 40 s). Its error is
     *          still ~35 %, which is why the page offers terrain instead.
     *   dem  — 30 m distance grid, T 5 m: 22 % mean error against barometric
     *          truth on the same 11 tracks, bias −1.4 %. */
    var CLIMB = {
        baro: { grid: 't', step: 10000, med: 1, T: 2, dstep: 10, dT: 3 },
        gps:  { grid: 't', step: 10000, med: 9, T: 15, dstep: 20, dT: 10 },
        dem:  { grid: 'd', step: 30, med: 1, T: 5, dstep: 30, dT: 5 }
    };

    function classify(sr, from, to) {
        if (!sr.ele) return null;
        var ele = sr.ch.palt || sr.ele;
        if (sr.time) {
            var j = jitter(ele, sr.time, from, to);
            if (j) return j.med <= 0.8 && j.p90 <= 1.0 ? 'baro' : 'gps';
        }
        var g = resampleEle(sr, from, to, 10);
        if (g.length < 12) return 'gps';
        return noiseOf(g) < 0.6 ? 'baro' : 'gps';
    }

    function climb(sr, from, to, opts) {
        opts = opts || {};
        if (from == null) from = 0;
        if (to == null) to = sr.n - 1;
        var res = { gain: null, loss: null, min: null, max: null, threshold: null, source: null };
        if (!sr.ele || to <= from) return res;
        var min = Infinity, max = -Infinity, cnt = 0;
        for (var i = from; i <= to; i++) { var e = sr.ele[i]; if (!isNaN(e)) { cnt++; if (e < min) min = e; if (e > max) max = e; } }
        if (!cnt) return res;
        res.min = min; res.max = max;
        // A track whose every elevation is 0 carries no elevation (exporters write 0 for «unknown»).
        if (min === 0 && max === 0) { res.min = res.max = null; return res; }
        var src = opts.source || classify(sr, from, to) || 'gps';
        var P = CLIMB[src] || CLIMB.gps;
        // IGC loggers record pressure altitude beside GNSS altitude; it is the
        // smooth one, so the climb is counted on it (absolute heights stay GNSS).
        var ele = src !== 'dem' && sr.ch.palt ? sr.ch.palt : sr.ele;
        var useTime = P.grid === 't' && !!sr.time;
        var T = opts.threshold != null ? opts.threshold : (useTime ? P.T : P.dT);
        var gain = 0, loss = 0;
        for (var s = 0; s < sr.segStart.length; s++) {
            var a = Math.max(from, sr.segStart[s]);
            var b = Math.min(to, s + 1 < sr.segStart.length ? sr.segStart[s + 1] - 1 : sr.n - 1);
            if (b <= a) continue;
            var g = useTime ? timeGrid(ele, sr.time, a, b, P.step)
                            : resampleEle(ele === sr.ele ? sr : { dist: sr.dist, ele: ele }, a, b, useTime ? 10 : (P.grid === 'd' ? P.step : P.dstep));
            if (g.length < 2 && useTime) g = resampleEle(sr, a, b, P.dstep);
            var z = zigzag(median(g, P.med), T);
            gain += z.gain; loss += z.loss;
        }
        res.gain = gain; res.loss = loss; res.threshold = T; res.source = src;
        return res;
    }

    // ── speed ──────────────────────────────────────────────────────────────
    /* Speed per point, smoothed over a TIME window centred on the point
     * (default 9 s). Raw fix-to-fix speed at 1 Hz is noise: a 3 m jitter reads
     * as 11 km/h. Device speed (wheel sensor / Doppler) is used when present. */
    function speed(sr, win) {
        var n = sr.n, out = new Float32Array(n).fill(NaN);
        if (!sr.time) return out;
        var dev = sr.ch.speed, devOk = 0, i;
        if (dev) { for (i = 0; i < n; i++) if (!isNaN(dev[i])) devOk++; }
        if (dev && devOk > n * 0.8) { out.set(dev); return out; }
        win = (win || 9) * 1000;
        var t = sr.time, d = sr.geoDist, a = 0, b = 0;
        for (i = 0; i < n; i++) {
            if (isNaN(t[i])) continue;
            while (a < i && (isNaN(t[a]) || t[i] - t[a] > win / 2)) a++;
            if (b < i) b = i;
            while (b + 1 < n && !isNaN(t[b + 1]) && t[b + 1] - t[i] <= win / 2) b++;
            var dt = (t[b] - t[a]) / 1000;
            if (dt > 0) out[i] = (d[b] - d[a]) / dt;
            else if (i > 0) out[i] = out[i - 1];
        }
        return out;
    }

    /* Moving time: a gap between fixes counts as motion when the track actually
     * moved during it at walking pace or faster. Two rules, both measured:
     *   — speed over the interval ≥ vMin (0.5 m/s ≈ 1.8 km/h, GPXSee's figure)
     *   — a single interval longer than maxGap is a pause regardless (the
     *     watch was stopped and restarted somewhere else). */
    function moving(sr, from, to, opts) {
        opts = opts || {};
        var vMin = opts.vMin != null ? opts.vMin : 0.5;
        var maxGap = (opts.maxGap || 60) * 1000;
        var t = sr.time, d = sr.geoDist;
        if (!t) return null;
        var mv = 0, i;
        // Smooth distance over a short window so jitter while standing is not «motion».
        var winMs = 10000;
        var a = from;
        for (i = from + 1; i <= to; i++) {
            if (isNaN(t[i]) || isNaN(t[i - 1]) || isSegStart(sr, i)) continue;
            var dt = t[i] - t[i - 1];
            if (dt <= 0 || dt > maxGap) continue;
            while (a < i && (isNaN(t[a]) || t[i] - t[a] > winMs)) a++;
            var span = t[i] - t[a];
            var v = span > 0 ? (d[i] - d[a]) / (span / 1000) : (d[i] - d[i - 1]) / (dt / 1000);
            if (v >= vMin) mv += dt;
        }
        return mv / 1000;
    }

    function chanStats(arr, from, to, weightT) {
        if (!arr) return null;
        var s = 0, c = 0, mx = -Infinity, mn = Infinity;
        for (var i = from; i <= to; i++) {
            var v = arr[i];
            if (isNaN(v)) continue;
            s += v; c++;
            if (v > mx) mx = v;
            if (v < mn) mn = v;
        }
        return c ? { avg: s / c, max: mx, min: mn, n: c } : null;
    }

    function stats(sr, opts) {
        opts = opts || {};
        var from = opts.from != null ? opts.from : 0, to = opts.to != null ? opts.to : sr.n - 1;
        var r = { points: to - from + 1, distance: 0, elapsed: null, moving: null, avgSpeed: null, movingSpeed: null, maxSpeed: null,
                  climb: null, start: null, end: null, bounds: null, hr: null, cad: null, power: null, temp: null, segments: 0 };
        if (sr.n === 0 || to < from) return r;
        r.distance = sr.dist[to] - sr.dist[from];
        var t = sr.time;
        if (t) {
            var t0 = NaN, t1 = NaN;
            for (var i = from; i <= to; i++) if (!isNaN(t[i])) { t0 = t[i]; break; }
            for (var j = to; j >= from; j--) if (!isNaN(t[j])) { t1 = t[j]; break; }
            if (!isNaN(t0) && !isNaN(t1) && t1 >= t0) {
                r.start = t0; r.end = t1; r.elapsed = (t1 - t0) / 1000;
                r.moving = moving(sr, from, to, opts.moving);
                if (r.elapsed > 0) r.avgSpeed = r.distance / r.elapsed;
                if (r.moving > 0) r.movingSpeed = (sr.geoDist[to] - sr.geoDist[from]) / r.moving * (sr.devDist && sr.geoDist[to] > sr.geoDist[from] ? (r.distance / (sr.geoDist[to] - sr.geoDist[from])) : 1);
                if (!opts.noMax) {
                    var sp = opts.speed || speed(sr);
                    var mx = 0;
                    for (var k = from; k <= to; k++) if (sp[k] > mx) mx = sp[k];
                    r.maxSpeed = mx || null;
                }
            }
        }
        r.climb = climb(sr, from, to, opts.climb);
        var la0 = 90, la1 = -90, lo0 = 180, lo1 = -180;
        for (var q = from; q <= to; q++) {
            var la = sr.lat[q], lo = sr.lon[q];
            if (la < la0) la0 = la; if (la > la1) la1 = la;
            if (lo < lo0) lo0 = lo; if (lo > lo1) lo1 = lo;
        }
        r.bounds = [la0, lo0, la1, lo1];
        r.hr = chanStats(sr.ch.hr, from, to);
        r.cad = chanStats(sr.ch.cad, from, to);
        r.power = chanStats(sr.ch.power, from, to);
        r.temp = chanStats(sr.ch.temp, from, to);
        r.depth = chanStats(sr.ch.depth, from, to);
        r.segments = sr.segStart.filter(function (s) { return s >= from && s <= to; }).length || 1;
        return r;
    }

    // ── grade (%) over a distance window ───────────────────────────────────
    function grade(sr, win) {
        var n = sr.n, out = new Float32Array(n).fill(NaN);
        if (!sr.ele) return out;
        win = win || 60;
        var d = sr.dist, e = sr.ele, a = 0, b = 0;
        for (var i = 0; i < n; i++) {
            while (a < i && d[i] - d[a] > win / 2) a++;
            if (b < i) b = i;
            while (b + 1 < n && d[b + 1] - d[i] <= win / 2) b++;
            var aa = a, bb = b;
            while (aa < i && isNaN(e[aa])) aa++;
            while (bb > i && isNaN(e[bb])) bb--;
            var dd = d[bb] - d[aa];
            if (dd > win * 0.3 && !isNaN(e[aa]) && !isNaN(e[bb])) out[i] = (e[bb] - e[aa]) / dd * 100;
        }
        return out;
    }

    // ── splits (per km / per mile) ─────────────────────────────────────────
    /* Splits per unit. Each split's numbers come from stats() over its own range —
     * with the whole-track speed series NOT recomputed per split: doing that
     * made a 300 000-point ride spend 19 s here (775 splits × 300 000 points). */
    function splits(sr, unitM) {
        unitM = unitM || 1000;
        var out = [];
        if (sr.n < 2) return out;
        var d = sr.dist, t = sr.time, start = 0, next = unitM;
        for (var i = 1; i < sr.n; i++) {
            if (d[i] >= next || i === sr.n - 1) {
                var s = stats(sr, { from: start, to: i, noMax: true });
                out.push({ from: start, to: i, distance: d[i] - d[start], elapsed: s.elapsed, moving: s.moving,
                           gain: s.climb.gain, loss: s.climb.loss, hr: s.hr ? s.hr.avg : null, power: s.power ? s.power.avg : null, cad: s.cad ? s.cad.avg : null });
                start = i;
                next += unitM;
                if (!t) continue;
            }
        }
        return out;
    }

    // ── Douglas–Peucker in a local metric projection ───────────────────────
    function simplify(lat, lon, tol, keep) {
        var n = lat.length;
        if (n < 3) { var all = new Uint8Array(n); all.fill(1); return all; }
        var lat0 = 0;
        for (var i = 0; i < n; i++) lat0 += lat[i];
        lat0 = lat0 / n * RAD;
        var kx = 111320 * Math.cos(lat0), ky = 110574;
        var x = new Float64Array(n), y = new Float64Array(n);
        for (i = 0; i < n; i++) { x[i] = lon[i] * kx; y[i] = lat[i] * ky; }
        var mark = new Uint8Array(n);
        mark[0] = 1; mark[n - 1] = 1;
        if (keep) for (i = 0; i < keep.length; i++) mark[keep[i]] = 1;
        var stack = [0, n - 1], tol2 = tol * tol;
        while (stack.length) {
            var last = stack.pop(), first = stack.pop();
            var ax = x[first], ay = y[first], bx = x[last], by = y[last];
            var dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
            var best = -1, bestD = tol2;
            for (var k = first + 1; k < last; k++) {
                var px = x[k] - ax, py = y[k] - ay, dd;
                if (L2 === 0) dd = px * px + py * py;
                else {
                    var tt = (px * dx + py * dy) / L2;
                    if (tt < 0) tt = 0; else if (tt > 1) tt = 1;
                    var qx = px - tt * dx, qy = py - tt * dy;
                    dd = qx * qx + qy * qy;
                }
                if (dd > bestD) { bestD = dd; best = k; }
            }
            if (best > 0) {
                mark[best] = 1;
                stack.push(first, best, best, last);
            }
        }
        return mark;
    }

    /* Douglas–Peucker, run ONCE to the bottom: every point gets the distance at
     * which DP would pick it (capped by its parent's, so the ranking nests).
     * «Keep N points» is then a sort, not 28 bisection passes — measured on a
     * 300 000-point track: 2.2 s → a single pass. Garmin devices hold 10 000
     * points in a track and 250–500 in a route. */
    var rankCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
    function dpRank(lat, lon) {
        if (rankCache && rankCache.has(lat)) return rankCache.get(lat);
        var n = lat.length, eps = new Float64Array(n);
        if (n < 3) { eps.fill(Infinity); return eps; }
        var lat0 = 0, i;
        for (i = 0; i < n; i++) lat0 += lat[i];
        lat0 = lat0 / n * RAD;
        var kx = 111320 * Math.cos(lat0), ky = 110574;
        var x = new Float64Array(n), y = new Float64Array(n);
        for (i = 0; i < n; i++) { x[i] = lon[i] * kx; y[i] = lat[i] * ky; }
        eps[0] = eps[n - 1] = Infinity;
        var stack = [0, n - 1, Infinity];
        while (stack.length) {
            var cap = stack.pop(), last = stack.pop(), first = stack.pop();
            if (last - first < 2) continue;
            var ax = x[first], ay = y[first], dx = x[last] - ax, dy = y[last] - ay, L2 = dx * dx + dy * dy;
            var best = -1, bestD = -1;
            for (var k = first + 1; k < last; k++) {
                var px = x[k] - ax, py = y[k] - ay, dd;
                if (L2 === 0) dd = px * px + py * py;
                else {
                    var tt = (px * dx + py * dy) / L2;
                    if (tt < 0) tt = 0; else if (tt > 1) tt = 1;
                    var qx = px - tt * dx, qy = py - tt * dy;
                    dd = qx * qx + qy * qy;
                }
                if (dd > bestD) { bestD = dd; best = k; }
            }
            var e = Math.min(Math.sqrt(bestD), cap);
            eps[best] = e;
            stack.push(first, best, e, best, last, e);
        }
        if (rankCache) rankCache.set(lat, eps);
        return eps;
    }
    function simplifyTo(lat, lon, target, keep) {
        var n = lat.length;
        if (n <= target) { var all = new Uint8Array(n); all.fill(1); return { mask: all, tol: 0, kept: n }; }
        var eps = dpRank(lat, lon);
        var mask = new Uint8Array(n), forced = 0;
        if (keep) for (var q = 0; q < keep.length; q++) if (keep[q] >= 0 && keep[q] < n && !mask[keep[q]]) { mask[keep[q]] = 1; forced++; }
        if (!mask[0]) { mask[0] = 1; forced++; }
        if (!mask[n - 1]) { mask[n - 1] = 1; forced++; }
        var order = new Uint32Array(n);
        for (var i = 0; i < n; i++) order[i] = i;
        var sorted = Array.prototype.slice.call(order).sort(function (a, b) { return eps[b] - eps[a]; });
        var left = Math.max(0, target - forced), tol = 0;
        for (var j = 0; j < sorted.length && left > 0; j++) {
            var idx = sorted[j];
            if (mask[idx]) continue;
            mask[idx] = 1; left--; tol = eps[idx];
        }
        var kept = 0;
        for (i = 0; i < n; i++) kept += mask[i];
        // the largest deviation left is the rank of the best point NOT kept
        var dev = 0;
        for (j = 0; j < sorted.length; j++) { if (!mask[sorted[j]]) { dev = eps[sorted[j]]; break; } }
        return { mask: mask, tol: isFinite(dev) ? dev : 0, kept: kept };
    }

    /* Implausible fixes: a point whose arrival AND departure speeds are both far
     * above what the rest of the track does (a jump out and back). One rule
     * with a floor: > max(4 × robust p95, 25 m/s on foot / 60 m/s generally). */
    function spikes(sr) {
        var out = [];
        var t = sr.time, n = sr.n;
        if (!t || n < 5) return out;
        var sp = [];
        for (var i = 1; i < n; i++) {
            var dt = (t[i] - t[i - 1]) / 1000;
            if (dt > 0 && !isSegStart(sr, i)) sp.push(dist(sr.lat[i - 1], sr.lon[i - 1], sr.lat[i], sr.lon[i]) / dt);
        }
        if (sp.length < 5) return out;
        var sorted = sp.slice().sort(function (a, b) { return a - b; });
        var p95 = sorted[Math.floor(sorted.length * 0.95)];
        var lim = Math.max(p95 * 4, 25);
        for (var k = 1; k < n - 1; k++) {
            if (isSegStart(sr, k) || isSegStart(sr, k + 1)) continue;
            var d1 = (t[k] - t[k - 1]) / 1000, d2 = (t[k + 1] - t[k]) / 1000;
            if (d1 <= 0 || d2 <= 0) continue;
            var v1 = dist(sr.lat[k - 1], sr.lon[k - 1], sr.lat[k], sr.lon[k]) / d1;
            var v2 = dist(sr.lat[k], sr.lon[k], sr.lat[k + 1], sr.lon[k + 1]) / d2;
            var direct = dist(sr.lat[k - 1], sr.lon[k - 1], sr.lat[k + 1], sr.lon[k + 1]) / (d1 + d2);
            if (v1 > lim && v2 > lim && direct < lim / 2) out.push(k);
        }
        return out;
    }

    root.GpsGeo = {
        dist: dist, vincenty: vincenty, series: series, stats: stats, climb: climb, speed: speed, moving: moving,
        grade: grade, splits: splits, simplify: simplify, simplifyTo: simplifyTo, dpRank: dpRank, spikes: spikes,
        _zigzag: zigzag, _median: median, _resampleEle: resampleEle, _noiseOf: noiseOf
    };
})();
